// src/components/resources/calculators/IncrementalityCalculator.tsx

'use client'

import { useState, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiLineChartLine from '~/icons/remix/generated/RiLineChartLine'
import RiPercentLine from '~/icons/remix/generated/RiPercentLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiAlertLine from '~/icons/remix/generated/RiAlertLine'
import RiMoneyDollarCircleLine from '~/icons/remix/generated/RiMoneyDollarCircleLine'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import { FAQ } from '~/components/shared/FAQ'
import { cn } from '~/lib/utils'
import { formatNumberForDisplay, formatText, formatPercent, formatNumber, formatCurrency } from '~/lib/utils/formatters'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import { calculateMetrics, type CalculatorInputs, type CalculatorResults } from './IncrementalityCalculator.logic'

/**
 * Configuration for the email template sent to users
 */
interface EmailTemplateConfig {
  /** Template identifier in the email service */
  templateAlias: string
  /** Recipient email address */
  to: string
  /** Sender email address */
  from: string
  /** Data model for the email template */
  templateModel: {
    /** Base template data */
    base: {
      name: string
      logo_url: string
      product_name: string
      product_url: string
      support_email: string
      sender_name: string
      company_name: string
      company_address: string
      email: string
    }
    /** Custom data specific to this calculator */
    custom: {
      calculator_type: string
      calculator_results: {
        metrics: CalculatorResults
        inputs: CalculatorInputs
      } | null
    }
  }
}

/**
 * FAQ items for the incrementality calculator
 */
const FAQ_ITEMS = [
  {
    question: "What is incrementality testing?",
    answer: "Incrementality testing is a method to measure the true causal impact of marketing activities by comparing a test group (exposed to marketing) against a control group (not exposed). It helps determine what would have happened without your marketing efforts, revealing the actual lift or incremental value generated by your campaigns. Unlike correlation-based methods, incrementality establishes causation through experimental design, making it the gold standard for measuring marketing effectiveness."
  },
  {
    question: "How is incrementality different from attribution?",
    answer: "Attribution assigns credit for conversions to specific touchpoints in the customer journey, while incrementality measures the true causal impact of marketing by comparing test and control groups. Attribution models (last-click, multi-touch, etc.) rely on correlations and can be misleading due to selection bias—they often give credit to channels that users were going to use anyway. Incrementality solves this by measuring the counterfactual: what would have happened without the marketing activity. For example, retargeting often shows high ROAS in attribution but low incrementality because it targets users already likely to convert."
  },
  {
    question: "What is a good incrementality lift percentage?",
    answer: "A good incrementality lift varies by industry, channel, and campaign objectives. Generally, a relative lift of 10-30% is considered good, while anything above 30% is excellent. However, even a 5% lift can be valuable if it's statistically significant and the campaign has large scale. Upper-funnel brand campaigns typically show 5-15% lift, mid-funnel consideration campaigns 15-25%, and lower-funnel conversion campaigns 20-40%. Mature markets tend to have lower incrementality than emerging ones. The key is comparing your lift to your cost—a 10% lift that costs 5% of revenue is better than a 20% lift that costs 25%."
  },
  {
    question: "How do I set up an incrementality test?",
    answer: "To set up an incrementality test: 1) Define clear objectives and success metrics (conversion rate, revenue, etc.), 2) Create truly randomized test and control groups using methods like tests, ghost ads, or geographic isolation, 3) Ensure the only difference between groups is exposure to the marketing activity being tested, 4) Determine appropriate sample sizes using power analysis (our calculator can help), 5) Run the test for a sufficient duration (typically 2-4 weeks), 6) Control for external factors like seasonality or other marketing activities, and 7) Analyze results using this calculator. For platform-specific tests, use Facebook's Conversion Lift, Google's Randomized Controlled Experiments, or TikTok's Brand Lift studies."
  },
  {
    question: "What sample size do I need for an incrementality test?",
    answer: "Sample size depends on your baseline conversion rate and the minimum lift you want to detect. For a baseline conversion rate of 1% and detecting a 20% relative lift (to 1.2%), you'd need approximately 35,000 users per group for 80% statistical power. For a 5% baseline rate, you'd need about 6,500 per group. Generally, you need larger samples for lower conversion rates or to detect smaller lifts. For most tests, aim for at least 1,000 users per group, though high-traffic campaigns may require 10,000+ per group for statistical validity. Use our calculator's sample size feature to determine your specific requirements based on your expected conversion rates."
  },
  {
    question: "What are the most common incrementality testing methodologies?",
    answer: "The most common incrementality testing methodologies include: 1) PSA (Public Service Announcement) Tests - where the control group sees charity/PSA ads instead of your ads, 2) Ghost Ads - where ad platforms record impressions that would have been served to control users without actually showing them ads, 3) Geographic Testing - comparing similar markets where one receives the marketing treatment and one doesn't, 4) Intent-to-Treat Analysis - analyzing users based on their assignment to test/control regardless of whether they actually saw ads, and 5) Holdout Tests - where a portion of your audience is excluded from specific marketing activities. Each method has trade-offs between accuracy, cost, and implementation complexity."
  },
  {
    question: "How do I interpret incrementality test results?",
    answer: "When interpreting incrementality test results, focus on: 1) Statistical Significance - ensure your p-value is <0.05 (95% confidence) before making decisions, 2) Practical Significance - a statistically significant result may still be too small to justify the marketing investment, 3) Incrementality ROI - compare the incremental revenue to the cost of the campaign, 4) Segment Analysis - examine how incrementality varies across different audience segments, 5) Channel Interactions - understand how this channel affects performance in other channels, and 6) Trend Analysis - track how incrementality changes over time or with scale. Remember that incrementality is not static—it can change with audience saturation, creative fatigue, or competitive activity."
  }
]

/**
 * Helper function to format a number with commas while typing
 * @param {string} value - The string value to format
 * @returns {string} Formatted string with commas
 */
function formatNumberWhileTyping(value: string): string {
  // Handle empty or decimal point only cases
  if (!value || value === '.') return value
  
  // Split by decimal point
  const parts = value.split('.')
  
  // Format the integer part with commas
  // Ensure parts[0] exists (it always will after split, but TypeScript needs assurance)
  const integerPart = parts[0] ?? ''
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  
  // Rejoin with decimal part if it exists
  return parts.length > 1 && parts[1] !== undefined 
    ? `${formattedIntegerPart}.${parts[1]}` 
    : formattedIntegerPart
}

/**
 * Type definition for calculator inputs
 */
type CalculatorInputValue = number | string

/**
 * Type definition for calculator inputs with string support for better UX
 */
interface CalculatorInputsWithStringSupport {
  controlGroupSize: CalculatorInputValue
  controlGroupConversions: CalculatorInputValue
  testGroupSize: CalculatorInputValue
  testGroupConversions: CalculatorInputValue
  averageOrderValue: CalculatorInputValue
  marketingCost: CalculatorInputValue
}

/**
 * Incrementality Calculator component
 * Allows users to calculate and visualize the true impact of marketing activities
 */
export function IncrementalityCalculator(): JSX.Element {
  // Initialize with default values for better UX
  const [inputs, setInputs] = useState<CalculatorInputsWithStringSupport>({
    controlGroupSize: 5000,
    controlGroupConversions: 250,
    testGroupSize: 5000,
    testGroupConversions: 300,
    averageOrderValue: 50,
    marketingCost: 1000
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const _isCalculating = useRef(false)
  
  // Track which fields are currently being edited and their raw values
  const [editingField, setEditingField] = useState<keyof CalculatorInputsWithStringSupport | null>(null)
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({})
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'incrementality-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])
  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  /**
   * Handles input changes with improved UX
   */
  const handleInputChange = (field: keyof CalculatorInputsWithStringSupport, value: string): void => {
    // Allow empty string for better UX during typing
    if (value === '') {
      setRawInputValues(prev => ({ ...prev, [field]: '' }))
      setInputs(prev => ({ ...prev, [field]: '' }))
      return
    }
    
    // For currency fields, strip currency symbols and commas
    if (field === 'averageOrderValue' || field === 'marketingCost') {
      value = value.replace(/[$,]/g, '')
    } else {
      // For non-currency fields, just strip commas
      value = value.replace(/,/g, '')
    }
    
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '')
    
    // Prevent multiple decimal points
    const parts = sanitizedValue.split('.')
    const cleanValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '')
    
    // Store the raw input value for reference
    setRawInputValues(prev => ({ ...prev, [field]: cleanValue }))
    
    // Store the value in state
    setInputs(prev => ({
      ...prev,
      [field]: cleanValue
    }))
    trackInteract('input_change')
  }

  /**
   * Handles input focus events to track which field is being edited
   */
  const handleInputFocus = (field: keyof CalculatorInputsWithStringSupport): void => {
    setEditingField(field)
    
    // Store the current value as raw input
    const currentValue = inputs[field]
    const rawValue = typeof currentValue === 'number' 
      ? currentValue.toString() 
      : currentValue.toString().replace(/,/g, '')
    
    setRawInputValues(prev => ({ ...prev, [field]: rawValue }))
  }

  /**
   * Handles input blur events to normalize values
   */
  const handleInputBlur = (field: keyof CalculatorInputsWithStringSupport): void => {
    setEditingField(null)
    const rawValue = rawInputValues[field] ?? ''
    
    // If empty, set to default minimum value
    if (rawValue === '') {
      if (field === 'controlGroupSize' || field === 'testGroupSize') {
        setInputs(prev => ({ ...prev, [field]: 1 }))
      } else {
        setInputs(prev => ({ ...prev, [field]: 0 }))
      }
      return
    }
    
    // Convert to number and apply appropriate formatting
    const numValue = parseFloat(rawValue)
    
    if (isNaN(numValue)) {
      setInputs(prev => ({ ...prev, [field]: field === 'controlGroupSize' || field === 'testGroupSize' ? 1 : 0 }))
      return
    }
    
    let normalizedValue: number
    
    switch (field) {
      case 'controlGroupSize':
      case 'testGroupSize':
        normalizedValue = Math.max(1, Math.round(numValue))
        break
      case 'controlGroupConversions':
      case 'testGroupConversions':
        normalizedValue = Math.max(0, Math.round(numValue))
        break
      case 'averageOrderValue':
      case 'marketingCost':
        normalizedValue = Math.max(0, parseFloat(numValue.toFixed(2)))
        break
      default:
        normalizedValue = numValue
    }
    
    setInputs(prev => ({ ...prev, [field]: normalizedValue }))
  }

  /**
   * Handles the calculation of incrementality metrics
   */
  const handleCalculate = (): void => {
    _isCalculating.current = true
    
    // Simulate a brief calculation delay for better UX
    setTimeout(() => {
      // Ensure all inputs are valid numbers before calculating
      const validatedInputs: CalculatorInputs = {
        controlGroupSize: Math.max(1, Number(inputs.controlGroupSize) || 1),
        controlGroupConversions: Math.max(0, Number(inputs.controlGroupConversions) || 0),
        testGroupSize: Math.max(1, Number(inputs.testGroupSize) || 1),
        testGroupConversions: Math.max(0, Number(inputs.testGroupConversions) || 0),
        averageOrderValue: Math.max(0, Number(inputs.averageOrderValue) || 0),
        marketingCost: Math.max(0, Number(inputs.marketingCost) || 0)
      }
      
      // Update inputs with validated values
      setInputs({
        controlGroupSize: validatedInputs.controlGroupSize,
        controlGroupConversions: validatedInputs.controlGroupConversions,
        testGroupSize: validatedInputs.testGroupSize,
        testGroupConversions: validatedInputs.testGroupConversions,
        averageOrderValue: validatedInputs.averageOrderValue,
        marketingCost: validatedInputs.marketingCost
      })
      
      const newResults = calculateMetrics(validatedInputs)
      setResults(newResults)
      setShowEmailCapture(true)
      sendResourceEvent('interact', {
        resourceType: 'calculator',
        resourceSlug: 'incrementality-calculator',
        engagementMs: getDeltaMs(),
        params: { interaction_type: 'calculate' }
      })
      sendResourceEvent('complete', {
        resourceType: 'calculator',
        resourceSlug: 'incrementality-calculator',
        engagementMs: getTotalMs(),
        params: {
          relative_lift: newResults.relativeLift,
          incremental_roi: newResults.incrementalROI
        }
      })
      _isCalculating.current = false
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.getElementById('incrementality-results')
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }, 500)
  }

  /**
   * Prepares the email template configuration based on current state
   */
  const emailTemplate = useMemo((): EmailTemplateConfig => {
    // Base email template configuration
    const baseTemplate: EmailTemplateConfig = {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: '',
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
          email: ''
        },
        custom: {
          calculator_type: 'Incrementality',
          calculator_results: results ? {
            metrics: results,
            inputs: {
              controlGroupSize: Number(inputs.controlGroupSize),
              controlGroupConversions: Number(inputs.controlGroupConversions),
              testGroupSize: Number(inputs.testGroupSize),
              testGroupConversions: Number(inputs.testGroupConversions),
              averageOrderValue: Number(inputs.averageOrderValue),
              marketingCost: Number(inputs.marketingCost)
            }
          } : null
        }
      }
    }

    return baseTemplate
  }, [results, inputs])

  /**
   * Configuration for the email capture component
   */
  const emailCaptureConfig: EmbeddedEmailCaptureProps = {
    preset: 'indigo',
    content: {
      title: {
        text: "Get Your Incrementality Analysis"
      },
      description: {
        text: "We'll send you a detailed breakdown of your metrics and personalized recommendations."
      },
      button: {
        text: "Get Analysis",
        loadingText: "Preparing Analysis...",
        icon: null,
        className: "flex w-auto items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      },
      input: {
        label: "Work Email",
        placeholder: "Enter your work email"
      },
      messages: {
        success: "Analysis sent!",
        error: "Please enter a valid email address."
      }
    },
    template: emailTemplate,
    analytics: {
      enabled: true,
      category: 'calculators',
      action: 'email_capture',
      label: 'incrementality_calculator'
    },
    aria: {
      label: "Email capture form for incrementality analysis"
    }
  }

  /**
   * Formats input values for display with improved UX
   */
  const getDisplayValue = (field: keyof CalculatorInputsWithStringSupport): string => {
    // If the field is currently being edited, show the formatted raw value
    if (editingField === field) {
      const rawValue = rawInputValues[field] ?? ''
      
      // Format with commas while typing for better readability
      if (rawValue) {
        return formatNumberWhileTyping(rawValue)
      }
      return rawValue
    }
    
    const value = inputs[field]
    
    // Handle empty values
    if (value === '') return ''
    
    // Format based on field type
    if (field === 'averageOrderValue' || field === 'marketingCost') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      return isNaN(numValue) ? '0' : formatCurrency(numValue, 2, false)
    } else {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      return isNaN(numValue) ? '0' : formatNumber(numValue, 0)
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      {/* Calculator Form */}
      <div className="mb-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Incrementality Calculator
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Measure the true impact of your marketing by comparing test and control groups.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Control Group
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="controlGroupSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Control Group Size
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    id="controlGroupSize"
                    value={getDisplayValue('controlGroupSize')}
                    onChange={(e) => handleInputChange('controlGroupSize', e.target.value)}
                    onFocus={() => handleInputFocus('controlGroupSize')}
                    onBlur={() => handleInputBlur('controlGroupSize')}
                    className="block w-full rounded-md border-gray-300 pr-12 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="controlGroupSize-description"
                    min="1"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">users</span>
                  </div>
                </div>
                <p id="controlGroupSize-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Number of users in the control group (not exposed to marketing)
                </p>
              </div>
              <div>
                <label htmlFor="controlGroupConversions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Control Group Conversions
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    id="controlGroupConversions"
                    value={getDisplayValue('controlGroupConversions')}
                    onChange={(e) => handleInputChange('controlGroupConversions', e.target.value)}
                    onFocus={() => handleInputFocus('controlGroupConversions')}
                    onBlur={() => handleInputBlur('controlGroupConversions')}
                    className="block w-full rounded-md border-gray-300 pr-12 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="controlGroupConversions-description"
                    min="0"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">conversions</span>
                  </div>
                </div>
                <p id="controlGroupConversions-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Number of conversions in the control group
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Test Group
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="testGroupSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Test Group Size
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    id="testGroupSize"
                    value={getDisplayValue('testGroupSize')}
                    onChange={(e) => handleInputChange('testGroupSize', e.target.value)}
                    onFocus={() => handleInputFocus('testGroupSize')}
                    onBlur={() => handleInputBlur('testGroupSize')}
                    className="block w-full rounded-md border-gray-300 pr-12 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="testGroupSize-description"
                    min="1"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">users</span>
                  </div>
                </div>
                <p id="testGroupSize-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Number of users in the test group (exposed to marketing)
                </p>
              </div>
              <div>
                <label htmlFor="testGroupConversions" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Test Group Conversions
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <input
                    type="text"
                    id="testGroupConversions"
                    value={getDisplayValue('testGroupConversions')}
                    onChange={(e) => handleInputChange('testGroupConversions', e.target.value)}
                    onFocus={() => handleInputFocus('testGroupConversions')}
                    onBlur={() => handleInputBlur('testGroupConversions')}
                    className="block w-full rounded-md border-gray-300 pr-12 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="testGroupConversions-description"
                    min="0"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">conversions</span>
                  </div>
                </div>
                <p id="testGroupConversions-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Number of conversions in the test group
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Business Metrics
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="averageOrderValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Average Order Value
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="averageOrderValue"
                    value={getDisplayValue('averageOrderValue')}
                    onChange={(e) => handleInputChange('averageOrderValue', e.target.value)}
                    onFocus={() => handleInputFocus('averageOrderValue')}
                    onBlur={() => handleInputBlur('averageOrderValue')}
                    className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="averageOrderValue-description"
                    min="0"
                  />
                </div>
                <p id="averageOrderValue-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Average revenue per conversion
                </p>
              </div>
              <div>
                <label htmlFor="marketingCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Marketing Cost
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    id="marketingCost"
                    value={getDisplayValue('marketingCost')}
                    onChange={(e) => handleInputChange('marketingCost', e.target.value)}
                    onFocus={() => handleInputFocus('marketingCost')}
                    onBlur={() => handleInputBlur('marketingCost')}
                    className="block w-full rounded-md border-gray-300 pl-7 focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                    aria-describedby="marketingCost-description"
                    min="0"
                  />
                </div>
                <p id="marketingCost-description" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Total cost of the marketing campaign
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-end sm:col-span-2">
            <button
              onClick={handleCalculate}
              className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
            >
              Calculate Incrementality
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mb-8 space-y-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
          >
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Incrementality Results
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Statistical confidence: {formatPercent(results.statisticalSignificance / 100, 1, false)}%
              </p>
            </div>

            {/* Primary Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Key Metrics
              </h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Relative Lift</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {formatPercent(results.relativeLift / 100, 2)}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Incremental Revenue</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {formatNumberForDisplay(results.incrementalRevenue, 'currency', 2)}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Incremental ROI</dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {formatPercent(results.incrementalROI / 100, 2)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Secondary Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detailed Metrics
              </h3>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Control Conversion Rate</dt>
                  <dd className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
                    {formatPercent(results.controlConversionRate / 100, 2)}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Test Conversion Rate</dt>
                  <dd className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
                    {formatPercent(results.testConversionRate / 100, 2)}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Absolute Lift</dt>
                  <dd className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
                    {formatPercent(results.absoluteLift / 100, 2)}
                  </dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Incremental Conversions</dt>
                  <dd className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
                    {formatNumberForDisplay(Math.round(results.incrementalConversions), 'number', 0)}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Recommendations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recommendations
              </h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {results.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={cn(
                      "rounded-lg p-5 shadow-sm transition-all hover:shadow-md",
                      rec.type === 'primary' ? "bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30" : 
                      rec.type === 'optimization' ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30" : 
                      "bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                    )}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <span className={cn(
                          "flex h-10 w-10 items-center justify-center rounded-full",
                          rec.type === 'primary' ? "bg-indigo-100 dark:bg-indigo-800" :
                          rec.type === 'optimization' ? "bg-amber-100 dark:bg-amber-800" :
                          "bg-gray-100 dark:bg-gray-700"
                        )}>
                          {rec.icon === 'chart' && <RiLineChartLine className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />}
                          {rec.icon === 'attribution' && <RiPercentLine className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
                          {rec.icon === 'strategy' && <RiMoneyDollarCircleLine className="h-6 w-6 text-gray-600 dark:text-gray-400" />}
                          {rec.icon === 'warning' && <RiAlertLine className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
                          {!rec.icon && <RiLightbulbLine className="h-6 w-6 text-gray-600 dark:text-gray-400" />}
                        </span>
                      </div>
                      <div className="ml-4">
                        <h4 className={cn(
                          "text-sm font-medium mb-1",
                          rec.type === 'primary' ? "text-indigo-800 dark:text-indigo-200" :
                          rec.type === 'optimization' ? "text-amber-800 dark:text-amber-200" :
                          "text-gray-800 dark:text-gray-200"
                        )}>
                          {rec.type === 'primary' ? "Key Action" : 
                           rec.type === 'optimization' ? "Optimization" : 
                           "Helpful Tip"}
                        </h4>
                        <p className={cn(
                          "text-sm",
                          rec.type === 'primary' ? "text-indigo-700 dark:text-indigo-300" :
                          rec.type === 'optimization' ? "text-amber-700 dark:text-amber-300" :
                          "text-gray-700 dark:text-gray-300"
                        )}>
                          {formatText(rec.text)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Capture */}
            {showEmailCapture && results && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20"
              >
                <EmbeddedEmailCapture {...emailCaptureConfig} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section */}
      <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ 
          items={FAQ_ITEMS} 
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "xl" }}
        />
      </div>
    </div>
  )
}

export default IncrementalityCalculator
